import { PureComponent } from "react";
import { DatePicker as KDatePicker } from "@progress/kendo-react-dateinputs";
import { Input as KInput } from "@progress/kendo-react-inputs";
import moment from "moment";
import { v4 as uuid } from "uuid";

import { reformatDateString, toFormattedDate } from "../../utils/date";
import { ErrorWrap } from "../ErrorWrap/ErrorWrap";

import { CustomCalendar } from "./customCalendar";
import { CustomDateInput } from "./customDateInput";

import "./DatePicker.scss";

import type { FocusEvent, KeyboardEvent, SyntheticEvent } from "react";
import type { DatePickerChangeEvent, DatePickerHandle } from "@progress/kendo-react-dateinputs";
import type { InputChangeEvent, InputHandle } from "@progress/kendo-react-inputs";
import type { ValidationErrors } from "../../models";

interface DatePickerProps {
  // we can prevent calendar opening because of the validation error in other field
  checkPreventOpen?: (fieldName: string) => boolean;
  className?: string;
  defaultValue?: Date;
  disabled?: boolean;
  error?: ValidationErrors; // -> error
  field: string;
  groupClassName?: string;
  inputClassName?: string;
  inputId?: string;
  label?: string;
  labelClassName?: string;
  name?: string;
  noWrap?: boolean; // for the grid cells: no need in the wrapping
  onBlur?: (fieldName: string, value?: string, e?: FocusEvent<HTMLInputElement>) => boolean;
  onChange?: (fieldName: string, value?: string, event?: SyntheticEvent<any>) => void;
  onFocus?: (fieldName: string, inputFocused: boolean) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>, fieldName: string, value?: string) => void;
  value?: string;
  max?: Date;
  placeholder?: string;
  dataTestId: string;
}

interface DatePickerState {
  strValue?: string;
  dateValue: Date | null;
  propsValue?: string; // for comparison with previous props
}

export class DatePicker extends PureComponent<DatePickerProps, DatePickerState> {
  datePicker: DatePickerHandle | null = null;
  input: InputHandle | null = null;
  valueWasPasted = false;

  constructor(opts: DatePickerProps) {
    super(opts);

    this.onChangeDatePicker = this.onChangeDatePicker.bind(this);
    this.onChangeInput = this.onChangeInput.bind(this);
    this.onBlurInput = this.onBlurInput.bind(this);
    this.onFocusInput = this.onFocusInput.bind(this);
    this.onFocusDatePicker = this.onFocusDatePicker.bind(this);
    this.onBlurDatePicker = this.onBlurDatePicker.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPasteInput = this.onPasteInput.bind(this);

    this.state = {
      dateValue: toFormattedDate(opts.value),
      strValue: opts.value,
    };
  }

  componentDidMount() {
    this.valueWasPasted = false;
  }

  static getDerivedStateFromProps(nextProps: DatePickerProps, prevState: DatePickerState) {
    if (nextProps.value !== prevState.propsValue) {
      return {
        dateValue: toFormattedDate(nextProps.value),
        propsValue: nextProps.value,
        strValue: nextProps.value,
      };
    }

    return null;
  }

  static toMMDDYYYYDate(date: string | Date | null | undefined): string {
    if (typeof date !== "undefined" && date !== null) {
      return reformatDateString(moment(date).format("MM/DD/Y"));
    }

    return "";
  }

  onChangeDatePicker(event: DatePickerChangeEvent) {
    try {
      this.datePicker?.element?.querySelectorAll(".k-state-focused").forEach((el) => {
        el.className = el.className.replace("k-state-focused", "");
      });
    } catch (error) {
      // Ignore errors when removing focused state
    }
    const val = event.target.value;
    const formatted = DatePicker.toMMDDYYYYDate(val);

    this.setState(
      {
        dateValue: val,
        strValue: formatted,
      },
      () => {
        if (this.props.onChange) {
          this.props.onChange(this.props.field, formatted, event.syntheticEvent);
        }
      },
    );
  }

  onChangeInput(event: InputChangeEvent) {
    let val = event.target.value as unknown as string;

    if (this.valueWasPasted) {
      this.valueWasPasted = false;
      // skip spaces
      val = val.trim();
    }

    this.setState({
      dateValue: toFormattedDate(val),
      strValue: val,
    });
  }

  /** Value the input commits on blur: typed text normalized to MM/DD/YYYY. */
  getCommitValue() {
    const val = this.state.strValue;
    const formatted = val ? reformatDateString(val) : "";
    return formatted || val;
  }

  onBlurInput(event) {
    const finalValue = this.getCommitValue();

    // the same date can be typed in the different format
    if (finalValue !== this.state.strValue) {
      this.setState({ strValue: finalValue });
    }

    const targetClassName = event.relatedTarget?.className || "";
    const blurToCalendar = targetClassName && targetClassName.indexOf("k-calendar") !== -1;

    if (this.props.onChange) {
      this.props.onChange(this.props.field, finalValue, event);
    }

    if (this.props.onBlur && !blurToCalendar) {
      this.props.onBlur(this.props.field, finalValue, event);
    }
  }

  onFocusInput() {
    if (this.props.onFocus) {
      this.props.onFocus(this.props.field, true);
    }
  }

  onFocusDatePicker() {
    if (this.props.onFocus) {
      this.props.onFocus(this.props.field, false);
    }

    if (this.props.field === "dateOfBirth") {
      const datePickers = document.querySelectorAll(".k-calendar");
      for (let ind = 0, len = datePickers.length; ind < len; ind++) {
        const datePicker = datePickers && datePickers[ind];
        if (datePicker) {
          datePicker.setAttribute("data-private", "");
        }
      }
    }
  }

  onBlurDatePicker(event) {
    if (this.props.onBlur) {
      // true = cannot blur. return focus back to input
      if (this.props.onBlur(this.props.field, this.state.strValue, event)) {
        if (this.input) {
          this.input.focus();
        }
      }
    }
  }

  onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (this.props.onKeyDown) {
      this.props.onKeyDown(event, this.props.field, this.getCommitValue());
    }
  }

  onPasteInput = () => {
    // want to handle text during change event. Text can be inserted as the part of the control value so I don't want to replace entire text.
    this.valueWasPasted = true;
  };

  setDatePickerRef = (ref: KDatePicker | null) => {
    this.datePicker = ref;
    const el = ref ? (this.datePicker as any)._element : null;

    // there is no any valid way to handle onOpen event except _element
    // I force input focusing during onMouseDown to correctly have blur event for the previously focused element
    if (this.datePicker && el) {
      el.onmousedown = this.onMouseDown;
      el.onclick = this.onClick;

      // Add data-testid to the DOM element
      el.setAttribute("data-testid", `${this.props.dataTestId}-calendar`);
    }
  };

  // focus input when we try to open calendar
  onMouseDown = () => {
    if (!this.props.disabled && this.input) {
      this.input.focus();
    }
  };

  // do not allow to open calendar if we have client-side validation errors in other fields
  onClick = (event) => {
    if (this.props.disabled || this.props.checkPreventOpen?.(this.props.field)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  render() {
    const id = `id-${uuid()}`;
    const { error, noWrap } = this.props;
    const disabled = typeof this.props.disabled !== "undefined" && this.props.disabled;
    const className =
      !disabled ?
        `form-input ${this.props.inputClassName ? this.props.inputClassName : "date-control-input"} k-input wrapped-input-control ${this.state.strValue ? " has-text" : ""}`
      : `form-input k-state-disabled ${this.props.inputClassName ? this.props.inputClassName : "date-control-input"} k-input wrapped-input-control ${this.state.strValue ? " has-text" : ""}`;
    const wrapperClass = `${this.props.groupClassName} tc-datepicker`;

    const input = (
      <KInput
        autoComplete="off"
        className={className}
        data-lpignore="true"
        data-testid={`${this.props.dataTestId}-input`}
        disabled={disabled}
        id={this.props.inputId}
        onBlur={this.onBlurInput}
        onChange={disabled ? undefined : this.onChangeInput}
        onFocus={this.onFocusInput}
        onKeyDown={this.onKeyDown}
        onPaste={this.onPasteInput}
        ref={(ref) => {
          this.input = ref;
        }}
        placeholder={this.props.placeholder || "MM/DD/YYYY"}
        readOnly={disabled}
        value={this.state.strValue ? this.state.strValue : ""}
      />
    );

    const control =
      disabled ?
        <div className={wrapperClass} data-testid={`${this.props.dataTestId}-container`}>
          <div className="fullDiv">{input}</div>
        </div>
      : <div className={wrapperClass} data-testid={`${this.props.dataTestId}-container`}>
          <div id={id} className="inputDiv">
            {input}
          </div>
          <div className="dateDiv">
            <KDatePicker
              calendar={CustomCalendar}
              className={this.props.className}
              dateInput={CustomDateInput}
              defaultValue={this.props.defaultValue}
              disabled={disabled}
              format="M/d/yyyy"
              id={id}
              max={this.props.max}
              name={this.props.name}
              onBlur={this.onBlurDatePicker}
              onChange={this.onChangeDatePicker}
              onFocus={this.onFocusDatePicker}
              ref={this.setDatePickerRef}
              tabIndex={-1}
              value={this.state.dateValue}
            />
          </div>
        </div>;

    return noWrap ? control : (
        <ErrorWrap error={error} withLabel>
          <div className="k-textbox-container">
            {this.props.label && (
              <label className={`k-label ${this.props.labelClassName || ""}`} htmlFor={id}>
                {this.props.label}
              </label>
            )}
            {control}
          </div>
        </ErrorWrap>
      );
  }
}
